import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  sakSchema,
  saksoppgaverSchema,
  type EndreDeloppgave,
  type LagreSaksdetaljer,
  type NyTidsforing,
  type NyttInnlegg,
  type Reaksjon,
  type SakDto,
  type SaksoppgaverDto,
  type Sakstype,
} from '@es/shared';
import { apiHent, ApiFeil } from '../api/klient.js';
import { hentToken } from '../auth/oidc.js';
import { miljo } from '../felles/miljo.js';

/** Alle saksspørringer ligger under samme nøkkel, så hver endring henter dem på nytt. */
const SAKER = ['saker'] as const;

export function useSak(type: Sakstype, id: string): UseQueryResult<SakDto> {
  return useQuery({
    queryKey: [...SAKER, type, id],
    queryFn: () => apiHent(`/saker/${type}/${id}`, sakSchema),
    // En kollega kan ha skrevet i diskusjonen.
    refetchInterval: 30 * 1000,
  });
}

export function useSaksoppgaver(aktiv = true): UseQueryResult<SaksoppgaverDto> {
  return useQuery({
    queryKey: [...SAKER, 'oppgaver'],
    queryFn: () => apiHent('/saker/oppgaver', saksoppgaverSchema),
    enabled: aktiv,
  });
}

function useEndring<T, R = void>(kall: (data: T) => Promise<R>): UseMutationResult<R, unknown, T> {
  const koe = useQueryClient();
  return useMutation({
    mutationFn: kall,
    onSuccess: () => {
      void koe.invalidateQueries({ queryKey: SAKER });
      // Vedlikeholdsappens egne lister viser status og antall som en sak endrer.
      void koe.invalidateQueries({ queryKey: ['vedlikehold'] });
    },
  });
}

const id = z.object({ id: z.string() });
const send = (sti: string, metode: 'POST' | 'PUT' | 'DELETE', data?: unknown) =>
  apiHent(sti, z.unknown(), {
    method: metode,
    ...(data === undefined ? {} : { body: JSON.stringify(data) }),
  });

export interface Sakreferanse {
  readonly type: Sakstype;
  readonly id: string;
}

export const useNyttInnlegg = (s: Sakreferanse) =>
  useEndring((data: NyttInnlegg) =>
    apiHent(`/saker/${s.type}/${s.id}/innlegg`, id, { method: 'POST', body: JSON.stringify(data) }),
  );

export const useSlettInnlegg = () =>
  useEndring(async (innleggId: string) => {
    await send(`/saker/innlegg/${innleggId}`, 'DELETE');
  });

export const useVeksleReaksjon = () =>
  useEndring(async ({ innleggId, emoji }: { innleggId: string; emoji: Reaksjon }) => {
    await send(`/saker/innlegg/${innleggId}/reaksjoner`, 'PUT', { emoji });
  });

export const useLastOppVedlegg = (s: Sakreferanse) =>
  useEndring((fil: File) => {
    const skjema = new FormData();
    skjema.append('fil', fil);
    return apiHent(`/saker/${s.type}/${s.id}/vedlegg`, id, { method: 'POST', body: skjema });
  });

export const useSlettVedlegg = () =>
  useEndring(async (vedleggId: string) => {
    await send(`/saker/vedlegg/${vedleggId}`, 'DELETE');
  });

export const useLagreDetaljer = (s: Sakreferanse) =>
  useEndring(async (data: LagreSaksdetaljer) => {
    await send(`/saker/${s.type}/${s.id}`, 'PUT', data);
  });

export const useNyDeloppgave = (s: Sakreferanse) =>
  useEndring(async (tekst: string) => {
    await send(`/saker/${s.type}/${s.id}/deloppgaver`, 'POST', { tekst });
  });

export const useEndreDeloppgave = () =>
  useEndring(async ({ deloppgaveId, ...data }: { deloppgaveId: string } & EndreDeloppgave) => {
    await send(`/saker/deloppgaver/${deloppgaveId}`, 'PUT', data);
  });

export const useSlettDeloppgave = () =>
  useEndring(async (deloppgaveId: string) => {
    await send(`/saker/deloppgaver/${deloppgaveId}`, 'DELETE');
  });

export const useForTid = (s: Sakreferanse) =>
  useEndring(async (data: NyTidsforing) => {
    await send(`/saker/${s.type}/${s.id}/tid`, 'POST', data);
  });

export const useSlettTid = () =>
  useEndring(async (tidId: string) => {
    await send(`/saker/tid/${tidId}`, 'DELETE');
  });

/**
 * Et vedlegg som en blob. Ikke via `apiHent`: svaret er en fil, ikke JSON, og et
 * `<img src>` kan ikke sende tokenet selv.
 */
export async function hentVedlegg(vedleggId: string): Promise<Blob> {
  const token = await hentToken();
  const svar = await fetch(`${miljo().apiBaseUrl}/api/v1/saker/vedlegg/${vedleggId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!svar.ok) throw new ApiFeil(svar.status, null, 'Vedlegget kunne ikke hentes.');
  return svar.blob();
}

export function useVedleggsbilde(vedleggId: string): UseQueryResult<string> {
  return useQuery({
    queryKey: [...SAKER, 'bilde', vedleggId],
    queryFn: async () => URL.createObjectURL(await hentVedlegg(vedleggId)),
    staleTime: Infinity,
  });
}

/** Laster ned et vedlegg med navnet det hadde. */
export async function lastNed(vedleggId: string, filnavn: string): Promise<void> {
  const url = URL.createObjectURL(await hentVedlegg(vedleggId));
  const lenke = document.createElement('a');
  lenke.href = url;
  lenke.download = filnavn;
  lenke.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10_000);
}
